"use client";

import React, { createContext, useContext, useState, useCallback } from "react";
import Toast, { ToastProps, ToastType } from "@/components/Toast";

interface ToastContextType {
  showToast: (toast: Omit<ToastProps, "id" | "onClose">) => void;
  showSuccess: (title: string, message: string, duration?: number) => void;
  showInfo: (title: string, message: string, duration?: number) => void;
  showWarning: (title: string, message: string, duration?: number) => void;
  showError: (title: string, message: string, duration?: number) => void;
}

interface ToastWithId extends ToastProps {
  id: string;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<ToastWithId[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (toast: Omit<ToastProps, "id" | "onClose">) => {
      const id = Math.random().toString(36).substring(2, 9);
      const newToast: ToastWithId = {
        ...toast,
        id,
        onClose: () => removeToast(id),
      };
      setToasts((prev) => [...prev, newToast]);
    },
    [removeToast]
  );

  const showSuccess = useCallback(
    (title: string, message: string, duration = 5000) => {
      showToast({ type: "success", title, message, duration });
    },
    [showToast]
  );

  const showInfo = useCallback(
    (title: string, message: string, duration = 5000) => {
      showToast({ type: "info", title, message, duration });
    },
    [showToast]
  );

  const showWarning = useCallback(
    (title: string, message: string, duration = 5000) => {
      showToast({ type: "warning", title, message, duration });
    },
    [showToast]
  );

  const showError = useCallback(
    (title: string, message: string, duration = 5000) => {
      showToast({ type: "error", title, message, duration });
    },
    [showToast]
  );

  return (
    <ToastContext.Provider
      value={{ showToast, showSuccess, showInfo, showWarning, showError }}
    >
      {children}

      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-3 pointer-events-none">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast {...toast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// Custom hook to use toast
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

export default ToastProvider;
